/**
 * Share current product/collection page via Web Share API or fallback menu.
 */
class ShareButtonComponent extends HTMLElement {
  connectedCallback() {
    this.shareUrl = this.dataset.shareUrl || window.location.href;
    this.shareTitle = this.dataset.shareTitle || document.title;
    this.shareText = this.dataset.shareText || '';

    this.mainBtn = this.querySelector('[data-share-main]');
    this.menu = this.querySelector('[data-share-menu]');
    this.status = this.querySelector('[data-share-status]');
    this.copyBtn = this.querySelector('[data-share-copy]');

    if (this.mainBtn) {
      this.mainBtn.addEventListener('click', (e) => this.handleMainShare(e));
    }
    if (this.copyBtn) {
      this.copyBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.copyLink();
      });
    }
    if (this.menu) {
      this.menu.querySelectorAll('[data-share-channel]').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const channel = el.getAttribute('data-share-channel');
          this.openChannel(channel);
        });
      });
    }

    document.addEventListener('click', this.#onDocClick);
    document.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocClick);
    document.removeEventListener('keydown', this.#onKeydown);
  }

  #onDocClick = (event) => {
    if (!this.contains(event.target)) this.closeMenu();
  };

  #onKeydown = (event) => {
    if (event.key === 'Escape') this.closeMenu();
  };

  /** Prefer native share only on phones/tablets; desktop always gets the menu (copy link, etc.). */
  prefersNativeShare() {
    if (typeof navigator.share !== 'function') return false;
    // Chrome desktop implements navigator.share but the sheet is weak / empty of "copy link".
    if (navigator.userAgentData && typeof navigator.userAgentData.mobile === 'boolean') {
      return navigator.userAgentData.mobile;
    }
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    const narrow = window.matchMedia('(max-width: 749px)').matches;
    const touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    return (coarse || narrow) && touch;
  }

  async handleMainShare(event) {
    event?.stopPropagation();

    if (this.prefersNativeShare()) {
      try {
        await navigator.share({
          title: this.shareTitle,
          text: this.shareText || this.shareTitle,
          url: this.shareUrl,
        });
        this.showStatus('Shared');
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        // Fall through to menu
      }
    }

    // Desktop (and native-share failure): open menu with Copy link + socials
    this.toggleMenu();
  }

  toggleMenu() {
    if (!this.menu) return;
    const open = this.menu.hasAttribute('hidden');
    if (open) {
      this.menu.removeAttribute('hidden');
      this.mainBtn?.setAttribute('aria-expanded', 'true');
    } else {
      this.closeMenu();
    }
  }

  closeMenu() {
    if (!this.menu || this.menu.hasAttribute('hidden')) return;
    this.menu.setAttribute('hidden', '');
    this.mainBtn?.setAttribute('aria-expanded', 'false');
  }

  async copyLink() {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(this.shareUrl);
      } else {
        const input = document.createElement('input');
        input.value = this.shareUrl;
        input.setAttribute('readonly', '');
        input.style.position = 'absolute';
        input.style.left = '-9999px';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }
      this.showStatus('Link copied');
      this.closeMenu();
    } catch {
      this.showStatus('Could not copy');
    }
  }

  openChannel(channel) {
    const url = encodeURIComponent(this.shareUrl);
    const title = encodeURIComponent(this.shareTitle);
    const text = encodeURIComponent(this.shareText || this.shareTitle);
    let href = '';

    switch (channel) {
      case 'facebook':
        href = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'x':
        href = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;
        break;
      case 'reddit':
        href = `https://www.reddit.com/submit?url=${url}&title=${title}`;
        break;
      case 'whatsapp':
        href = `https://wa.me/?text=${text}%20${url}`;
        break;
      case 'email':
        href = `mailto:?subject=${title}&body=${text}%0A%0A${url}`;
        break;
      default:
        return;
    }

    window.open(href, '_blank', 'noopener,noreferrer');
    this.closeMenu();
  }

  showStatus(message) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.removeAttribute('hidden');
    clearTimeout(this._statusTimer);
    this._statusTimer = setTimeout(() => {
      this.status.setAttribute('hidden', '');
    }, 2200);
  }
}

if (!customElements.get('share-button-component')) {
  customElements.define('share-button-component', ShareButtonComponent);
}
